import React from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';
import TermsOfServiceAr from './ar/TermsOfServiceAr';

const TermsOfService = () => {
  const { t, lang } = useI18n();
  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/login">
            <img
              src={`${process.env.PUBLIC_URL}/Logo-ArabCap.svg`}
              alt="ArabCap"
              className="h-8 w-auto"
            />
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher compact />
            <Link 
              to="/login" 
              className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
            >
              {t('Back to Login')}
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {lang === 'ar' ? <TermsOfServiceAr /> : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
          <h1 className="text-3xl font-extrabold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-slate-500 mb-8">Last updated: January 27, 2026</p>

          <div className="prose prose-slate max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">1. Agreement to Terms</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                By accessing or using ArabCap, you agree to be bound by these Terms of Service. If you do not agree, 
                you should not use the service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">2. Description of Service</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                ArabCap provides AI-powered subtitle generation for Arabic dialects, including Darija, Masri, Khaleeji, 
                and Modern Standard Arabic. Services include:
              </p>
              <ul className="list-disc ps-6 text-slate-600 space-y-2 mb-4">
                <li>Automatic speech recognition and transcription</li>
                <li>Subtitle editing and timing tools</li>
                <li>Export options in formats such as SRT and VTT</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">3. User Accounts</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                To use ArabCap, you must create an account. You are responsible for:
              </p>
              <ul className="list-disc ps-6 text-slate-600 space-y-2 mb-4">
                <li>Providing accurate and complete registration information</li>
                <li>Keeping your login credentials secure</li>
                <li>All activity under your account</li>
                <li>Notifying us immediately of unauthorized access</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">4. Acceptable Use</h2>
              <p className="text-slate-600 leading-relaxed mb-4">You agree not to:</p>
              <ul className="list-disc ps-6 text-slate-600 space-y-2 mb-4">
                <li>Upload content you do not have the right to use</li>
                <li>Violate any laws or regulations</li>
                <li>Infringe intellectual property rights</li>
                <li>Upload harmful, illegal, or offensive material</li>
                <li>Attempt unauthorized access to our systems</li>
                <li>Interfere with the service</li>
                <li>Use automated systems without permission</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">5. Content Ownership</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                <strong>Your Content:</strong> You retain ownership of all content you upload. By using ArabCap, 
                you grant us a limited license to process your content solely for providing subtitle generation services.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                <strong>Our Service:</strong> ArabCap and its original content, features, and technology remain 
                the property of ArabCap and are protected by intellectual property laws.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">6. Subscription and Payments</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Some features of ArabCap require a paid subscription. By subscribing, you agree to:
              </p>
              <ul className="list-disc ps-6 text-slate-600 space-y-2 mb-4">
                <li>Pay the applicable fees for your chosen plan</li>
                <li>Provide accurate billing information</li>
                <li>Automatic renewal of subscriptions unless cancelled</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mb-4">
                Refunds may be provided at our discretion. Contact support for refund requests.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">7. Free Tier and Free Trial</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                ArabCap offers a free tier with limited features and usage.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                The free trial is strictly limited to one per individual. Creating multiple accounts, using false 
                information, or attempting to bypass usage limits in order to obtain additional free trials constitutes 
                fraud and is strictly prohibited.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                If we detect fraudulent activity related to the free trial, we reserve the right to immediately suspend 
                or permanently terminate all associated accounts without notice.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may modify or discontinue the free tier with reasonable notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">8. Service Availability</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We strive to provide reliable service but do not guarantee uninterrupted availability. We may 
                perform maintenance or updates that temporarily affect service access. We are not liable for 
                any downtime or service interruptions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">9. Accuracy of Results</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                AI-generated subtitles may contain errors. You are responsible for reviewing and editing all 
                generated content before use. ArabCap is not liable for inaccuracies.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                To the maximum extent permitted by law, ArabCap shall not be liable for indirect, incidental, 
                consequential, or punitive damages, including loss of profits, revenue, data, or goodwill.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">11. Termination</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We may suspend or terminate accounts for violations of these terms. You may terminate your 
                account at any time. Upon termination, your right to use the service will immediately cease.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">12. Changes to Terms</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                We reserve the right to modify these terms at any time. Continued use of the service after 
                updates constitutes acceptance of the revised terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">13. Governing Law</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                These terms shall be governed by and construed in accordance with applicable laws, without 
                regard to conflict of law principles.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">14. Contact Us</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <p className="text-slate-600 leading-relaxed">
                <strong>Email:</strong> legal@arabcap.co
              </p>
            </section>
          </div>
        </div>
        )}

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/privacy" className="hover:text-slate-700 underline">{t('Privacy Policy')}</Link>
          <span className="mx-2">·</span>
          <Link to="/login" className="hover:text-slate-700 underline">{t('Back to Login')}</Link>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
